import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { TransformErrorEntry } from "@capture/shared";

export interface ExportRecordSummary {
  id: string;
  documentId: string;
  documentFilename: string;
  documentClassification: string | null;
  /** E-056 — displayId-led labels + batch identity (custom label wins,
   *  name/filename last resort). Optional for stale caches. */
  documentLabel?: string;
  batchId?: string;
  batchLabel?: string;
  destinationId: string;
  destinationName: string;
  adapterType: string;
  status: string;
  exportedAt: string | null;
  retryCount: number;
  lastError: string | null;
  /** F-036 — transform errors surfaced in the runtime log alongside lastError. */
  transformErrors: TransformErrorEntry[] | null;
  nextRetryAt: string | null;
  createdAt: string;
  /** E-027 — whether a rendered payload was persisted for this record. */
  hasPayload: boolean;
}

export interface ExportStats {
  [status: string]: number;
}

export function useExportRecords(filters?: {
  status?: string;
  destinationId?: string;
  documentId?: string;
  /** E-057 — batch-detail rollup click-through scope. */
  batchId?: string;
  /** E-057 — lane badge click-through scope (all batches of a job type). */
  jobTypeId?: string;
  limit?: number;
  offset?: number;
}) {
  const [records, setRecords] = useState<ExportRecordSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      if (filters?.destinationId) params.set("destinationId", filters.destinationId);
      if (filters?.documentId) params.set("documentId", filters.documentId);
      if (filters?.batchId) params.set("batchId", filters.batchId);
      if (filters?.jobTypeId) params.set("jobTypeId", filters.jobTypeId);
      if (filters?.limit) params.set("limit", String(filters.limit));
      if (filters?.offset) params.set("offset", String(filters.offset));

      const qs = params.toString() ? `?${params.toString()}` : "";
      const data = await api.get<{ records: ExportRecordSummary[]; total: number }>(
        `/export-records${qs}`,
      );
      setRecords(data.records);
      setTotal(data.total);
    } finally {
      setIsLoading(false);
    }
  }, [filters?.status, filters?.destinationId, filters?.documentId, filters?.batchId, filters?.jobTypeId, filters?.limit, filters?.offset]);

  useEffect(() => { load(); }, [load]);

  return { records, total, isLoading, refresh: load };
}

export function useExportStats() {
  const [stats, setStats] = useState<ExportStats>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    api.get<{ stats: ExportStats }>("/export-records/stats")
      .then((data) => setStats(data.stats))
      .finally(() => setIsLoading(false));
  }, []);

  return { stats, isLoading };
}

export function useRetryExport() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const retry = async (recordId: string): Promise<void> => {
    setIsSubmitting(true);
    try {
      await api.post(`/export-records/${recordId}/retry`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { retry, isSubmitting };
}

export function useBulkRetryExports() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const bulkRetry = async (destinationId?: string | null): Promise<{ count: number }> => {
    setIsSubmitting(true);
    try {
      const body = destinationId ? { destinationId } : {};
      return await api.post<{ count: number }>("/export-records/bulk-retry", body);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { bulkRetry, isSubmitting };
}

export function useDismissExport() {
  const dismiss = async (recordId: string): Promise<void> => {
    await api.post(`/export-records/${recordId}/dismiss`);
  };
  return { dismiss };
}
